import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Gift, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PACKAGE_OPTIONS = [
  { id: "pkg_casual", label: "Casual Private Lesson", value: 150, note: "1.5 hrs one-on-one" },
  { id: "pkg_starter", label: "4-Week DJ Starter", value: 500, note: "6 hrs · best value" },
];
const AMOUNTS = [50, 100, 150, 250, 500];
const EMPTY = { buyer_name: "", buyer_email: "", buyer_phone: "", recipient_name: "", message: "" };
const FIELDS = [["buyer_name", "Your name", "text"], ["buyer_email", "Your email (voucher is sent here)", "email"], ["buyer_phone", "Your mobile", "tel"], ["recipient_name", "Who's it for?", "text"]];

const OptionButton = ({ active, onClick, title, sub, testId }) => (
  <button type="button" onClick={onClick} className={`p-4 rounded-2xl border-2 text-left transition-colors ${active ? "border-black bg-black text-white" : "border-black/20 bg-white/60 text-black hover:border-black"}`} data-testid={testId}>
    <p className="font-display font-bold">{title}</p>
    {sub && <p className={`text-xs mt-0.5 ${active ? "text-white/60" : "text-black/60"}`}>{sub}</p>}
  </button>
);

export const GiftVoucherSection = () => {
  const [option, setOption] = useState("pkg_starter");
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const value = option.startsWith("amt_") ? Number(option.slice(4)) : PACKAGE_OPTIONS.find((p) => p.id === option).value;

  const buy = async () => {
    if (FIELDS.some(([k]) => !form[k].trim()) || !/^\S+@\S+\.\S+$/.test(form.buyer_email)) return toast.error("Please fill in your details");
    setLoading(true);
    try {
      const r = await axios.post(`${API}/vouchers/purchase`, { option, ...form });
      const c = await axios.post(`${API}/checkout/create`, { booking_id: r.data.booking_id, origin_url: window.location.origin });
      window.location.href = c.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start checkout, please try again");
      setLoading(false);
    }
  };

  return (
    <section id="jr-gift" className="relative py-20 md:py-28 bg-gradient-to-br from-pink-400 via-yellow-200 to-lime-300 overflow-hidden scroll-mt-20" data-testid="jr-gift-vouchers">
      <div className="relative max-w-6xl mx-auto px-6 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 items-start">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black text-lime-300 text-xs font-bold uppercase tracking-widest"><Gift className="w-4 h-4" /> The perfect Christmas gift</span>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-black tracking-tight leading-[0.95] mt-5">Give the gift of <span className="font-serif italic font-normal">music.</span></h2>
          <p className="text-black/75 mt-4 leading-relaxed">Grandparents, aunties, uncles and family: gift a Junior DJ Academy voucher this Christmas. It's emailed to you instantly as a printable voucher with a unique code, valid for 6 months.</p>
          <div className="mt-8 rotate-[-2deg] rounded-3xl p-6 bg-black text-white shadow-[10px_10px_0_rgba(0,0,0,0.25)] max-w-sm" data-testid="jr-gift-preview">
            <p className="text-lime-300 text-[10px] font-bold tracking-[0.3em]">DREAM MAKERS · JUNIOR DJ ACADEMY</p>
            <p className="font-display text-2xl font-bold mt-2">Gift Voucher</p>
            <p className="text-white/70 text-sm">For {form.recipient_name || "your young DJ"}</p>
            <p className="font-display text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-lime-300 to-pink-400 mt-3" data-testid="jr-gift-preview-value">${value}</p>
            <p className="mt-3 font-mono text-sm tracking-widest text-white/50">DMRG-XXXX-XXXX</p>
          </div>
        </motion.div>
        <div className="rounded-3xl p-6 md:p-8 bg-white/70 backdrop-blur-xl border border-white shadow-2xl space-y-5">
          <div>
            <p className="text-black font-semibold mb-2">Gift a package</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {PACKAGE_OPTIONS.map((p) => <OptionButton key={p.id} active={option === p.id} onClick={() => setOption(p.id)} title={`${p.label} · $${p.value}`} sub={p.note} testId={`jr-gift-option-${p.id}`} />)}
            </div>
          </div>
          <div>
            <p className="text-black font-semibold mb-2">Or choose an amount</p>
            <div className="grid grid-cols-5 gap-2">
              {AMOUNTS.map((a) => <OptionButton key={a} active={option === `amt_${a}`} onClick={() => setOption(`amt_${a}`)} title={`$${a}`} testId={`jr-gift-option-amt_${a}`} />)}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {FIELDS.map(([k, label, type]) => (
              <label key={k} className="block">
                <span className="text-black/70 text-sm">{label}</span>
                <Input type={type} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 bg-white border-black/20 text-black" data-testid={`jr-gift-${k}`} />
              </label>
            ))}
          </div>
          <label className="block">
            <span className="text-black/70 text-sm">Personal message (optional)</span>
            <Textarea rows={2} maxLength={300} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Merry Christmas! Can't wait to hear your first mix 🎧" className="mt-1 bg-white border-black/20 text-black" data-testid="jr-gift-message" />
          </label>
          <Button onClick={buy} disabled={loading} className="w-full bg-black hover:bg-black/85 text-white rounded-full py-7 text-base font-bold" data-testid="jr-gift-buy-btn">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : `Buy $${value} Gift Voucher`}
          </Button>
          <p className="text-black/60 text-xs text-center">Afterpay &amp; Zip available at checkout · Valid 6 months · Redeem when booking on this page</p>
        </div>
      </div>
    </section>
  );
};
